import { Circle, CircleMarker, Tooltip } from "react-leaflet";
import { LinearProgress } from "@mui/material";

import { MapMonitorDTO } from "../../DTOs/Telemetry/DrillBlastMapOverlayDTO";
import { StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { EXCLUSION_ZONE_STYLE, holeColour, MISFIRE_COLOUR, monitorColour, SENTRY_COLOUR } from "./mapStyles";
import { useDrillBlastOverlay } from "./useDrillBlastOverlay";

const reading = (value: number | null, limit: number | null, unit: string) =>
  value == null ? null : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}${limit != null ? ` (limit ${limit} ${unit})` : ""}`;

const MonitorTooltip = ({ monitor }: { monitor: MapMonitorDTO }) => (
  <div className="flex flex-col gap-1 min-w-[160px]">
    <strong>{monitor.name}</strong>
    {monitor.lastReadingAt == null
      ? <span>No readings yet</span>
      : (
        <>
          <span>Last reading {monitor.lastReadingAt.toFormat("d MMM HH:mm")}</span>
          {reading(monitor.ppvMmS, monitor.ppvLimit, "mm/s") && <span>PPV {reading(monitor.ppvMmS, monitor.ppvLimit, "mm/s")}</span>}
          {reading(monitor.airblastDbl, monitor.airblastLimit, "dBL") && <span>Airblast {reading(monitor.airblastDbl, monitor.airblastLimit, "dBL")}</span>}
          {monitor.percentOfLimit != null && (
            <>
              <LinearProgress variant="determinate" value={Math.min(100, monitor.percentOfLimit)}
                color={monitor.percentOfLimit > 100 ? "error" : monitor.percentOfLimit >= 75 ? "warning" : "success"} />
              <span>{Math.round(monitor.percentOfLimit)}% of its limit</span>
            </>
          )}
        </>
      )}
  </div>
);

/** Misfires, today's exclusion zones and sentries, vibration monitors and blast holes, above the pattern outlines. */
export const DrillBlastOverlayLayer = ({ visible }: { visible: boolean }) => {
  const overlay = useDrillBlastOverlay(visible);
  if (!visible || !overlay) return null;

  return (
    <StackedGroup rank={LAYER_RANK.blasting} version={overlay}>
      {overlay.plannedBlasts.filter(b => b.exclusionZoneM).map(b => (
        <Circle key={`exclusion-${b.patternBlastId}`} center={[b.latitude, b.longitude]} radius={b.exclusionZoneM!} pathOptions={EXCLUSION_ZONE_STYLE}>
          <Tooltip>{`${b.pattern} fires at ${b.plannedFireAt.toFormat("HH:mm")} · exclusion zone ${b.exclusionZoneM} m`}</Tooltip>
        </Circle>
      ))}
      {overlay.plannedBlasts.flatMap(b => b.sentries.map((s, i) => (
        <CircleMarker key={`sentry-${b.patternBlastId}-${i}`} center={[s.latitude, s.longitude]} radius={5}
          pathOptions={{ color: "#111827", weight: 1, fillColor: SENTRY_COLOUR, fillOpacity: 1 }}>
          <Tooltip>{["Sentry", s.person, s.description, b.pattern].filter(Boolean).join(" · ")}</Tooltip>
        </CircleMarker>
      )))}
      {overlay.holes.map(h => (
        <CircleMarker key={`hole-${h.patternBlastId}-${h.hole}`} center={[h.latitude, h.longitude]} radius={3}
          pathOptions={{ color: holeColour(h.status), weight: 1, fillColor: holeColour(h.status), fillOpacity: 0.9 }}>
          <Tooltip>{`${h.pattern} hole ${h.hole} · ${h.status}`}</Tooltip>
        </CircleMarker>
      ))}
      {overlay.monitors.map(m => (
        <CircleMarker key={`monitor-${m.id}`} center={[m.latitude, m.longitude]} radius={7}
          pathOptions={{ color: "#111827", weight: 1.5, fillColor: monitorColour(m.percentOfLimit), fillOpacity: 0.9 }}>
          <Tooltip><MonitorTooltip monitor={m} /></Tooltip>
        </CircleMarker>
      ))}
      {overlay.misfires.map(m => (
        <CircleMarker key={`misfire-${m.id}`} center={[m.latitude, m.longitude]} radius={9}
          pathOptions={{ color: "#FFFFFF", weight: 2, fillColor: MISFIRE_COLOUR, fillOpacity: 1 }}>
          <Tooltip>
            {`Open misfire · ${m.pattern}${m.holeReference ? ` hole ${m.holeReference}` : ""} · found ${m.foundAt.toFormat("d MMM HH:mm")}`
              + (m.atHole ? "" : " · hole position not recorded, shown at the pattern's middle")}
          </Tooltip>
        </CircleMarker>
      ))}
    </StackedGroup>
  );
};

export default DrillBlastOverlayLayer;
